import useFetch from "v4/hooks/useFetch";
import {useEffect, useMemo, useState} from "react";
import {generateUrl} from "v4/services/api.service";
import {EXPORT_DOWNLOAD, EXPORT_LIST} from "v4/data/apiRoutes";
import {downloadBlob} from "v4/utils";
import useExportMercure from "v4/features/front/export/components/ExportPopup/hooks/useExportMercure";
import useNotification from "v4/hooks/useNotification";
import {usePolTranslation} from "v4/hooks/usePolTranslation";

export default function useExportPopup() {
    const [loadingExports, readyExportsMercure, problemExports] = useExportMercure();
    const [{data, errorData, isError}, fetchExports] = useFetch();
    const [readyExports, setReadyExports] = useState({});
    const {addErrorNotification} = useNotification();
    const {t} = usePolTranslation();

    useEffect(() => {
        fetchExports({url: generateUrl(EXPORT_LIST)});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isError) return addErrorNotification(t(errorData?.message ?? 'error'));

        if (Object.keys(data?.files ?? {}).length > 0) {
            const _readyExports = {};
            Object.entries(data.files).forEach(([entity, exports]) => {
                exports.forEach(({id, requestedAt}) => {
                    _readyExports[id] = {
                        id,
                        entity,
                        requestedAt,
                        finished: true,
                        downloading: false,
                    }
                });
            });
            setReadyExports(prevState => ({...prevState, ..._readyExports}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, errorData, isError]);

    useEffect(() => {
        if (readyExportsMercure) {
            setReadyExports(prevState => ({...readyExportsMercure, ...prevState}));
        }
    }, [readyExportsMercure]);

    const exportDownload = (id) => () => {
        const exportData = readyExports[id];

        // If the blob (file) is already available, download it directly
        if (exportData.file) {
            return downloadBlob(exportData.file, `pol-export-${new Date().toISOString()}.csv`);
        }

        setReadyExports(prev => ({
            ...prev,
            [id]: {...prev[id], downloading: true, loaded: 0, status: 'indeterminate'} // I set the status to 'indeterminate' while we don't have the file size yet
        }));

        fetch(generateUrl(EXPORT_DOWNLOAD, {entity: exportData.entity, id}), {method: 'POST'})
            .then(res => {
                const fileSize = res.headers.get('Content-Length');
                let loaded = 0;

                // This response/ReadableStream allows us to get the file size and track the download progress
                // Check https://www.youtube.com/watch?v=FAswtajB67E
                return new Response(
                    new ReadableStream({
                        start(controller) {
                            const reader = res.body.getReader();

                            const processChunk = ({done, value}) => {
                                if (done) {
                                    setReadyExports(prev => ({
                                        ...prev,
                                        [id]: {...prev[id], downloading: false, fileSize, loaded, status: ''}
                                    }));
                                    return controller.close();
                                }

                                loaded += value.byteLength;

                                setReadyExports(prev => ({
                                    ...prev,
                                    [id]: {...prev[id], fileSize, loaded}
                                }));

                                controller.enqueue(value);
                                reader.read().then(processChunk);
                            };

                            reader.read().then(processChunk);
                        }
                    })
                );
            })
            .then(res => res.blob())
            .then(blob => {
                setReadyExports(prev => ({
                    ...prev,
                    [id]: {...prev[id], file: blob} // Store the blob in the export data to avoid re-fetching it
                }));
                downloadBlob(blob, `pol-export-${new Date().toISOString()}.csv`);
            });
    };

    const sortedLoadingExports = useMemo(() => Object.entries(loadingExports).sort(([idA, exportA], [idB, exportB]) => {
        const requestedAtA = new Date(exportA.requestedAt);
        const requestedAtB = new Date(exportB.requestedAt);
        return requestedAtB - requestedAtA;
    }).reduce((acc, [id, exportData]) => {
        acc[id] = exportData;
        return acc;
    }, {}), [loadingExports]);

    const sortedReadyExports = useMemo(() => Object.entries(readyExports).sort(([idA, exportA], [idB, exportB]) => {
        const requestedAtA = new Date(exportA.requestedAt);
        const requestedAtB = new Date(exportB.requestedAt);
        return requestedAtB - requestedAtA;
    }).reduce((acc, [id, exportData]) => {
        acc[id] = exportData;
        return acc;
    }, {}), [readyExports]);

    return {
        loadingExports: sortedLoadingExports,
        readyExports: sortedReadyExports,
        problemExports,
        exportDownload
    }
}
